import { PropsWithChildren, ReactNode } from 'react';
import { Menu } from '@mui/icons-material';
import { Box, BoxProps, Card } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { Documents, FuseSpeedDial, Notes, ToolbarContainer, dataModelIcons } from '/src/components';
import { useUserStore } from '/src/context';
import { DesktopLayout } from '/src/layouts';
import { Note } from '/src/lib/models';
import { CRUDMode, DataModelType } from '/src/lib/types';

export type ViewPageProps = PropsWithChildren & {
  /**
   * Data model to edit
   */
  modelType: DataModelType;
  /**
   * Data model display name
   */
  modelName: string;
  /**
   * Actions to pass into a FuSE Speed Dial component
   */
  speedDialActions?: React.ReactNode | React.ReactNode[];
  /**
   * Element ID
   */
  id?: string;
  /**
   * Display icon representing the given model type
   */
  icon?: React.ReactNode;
  /**
   * Optional sidebar component to display
   */
  sidebar?: ReactNode;
  /**
   * Display sidebar items
   */
  sidebarItems?: boolean[];
  /**
   * Flex value for sidebar
   */
  sidebarFlex?: string;
  /**
   * Flex value for page content
   */
  contentFlex?: string;
  /**
   * Toggles which sidebar items are open
   * @param items Sidebar items
   * @returns void
   */
  setSidebarItems?: (items: boolean[]) => void;
  /**
   * Modal components to display
   */
  modals?: ReactNode;
  /**
   * CSS class to use for text
   */
  textClassName?: string;
  /**
   * Page element width
   */
  width?: BoxProps['width'];
  /**
   * Config options for the Notes component
   */
  noteOptions?: {
    notes: Note[];
    mode: CRUDMode;
    onChange?: (notes: Note[]) => void;
    title?: string;
    tooltip?: string;
  };
  /**
   * Config options for the Documents component
   */
  documentOptions?: {
    files: string[];
    mode: CRUDMode;
    onChange?: (files: string[]) => void;
    title?: string;
    tooltip?: string;
  };
  /**
   * Flex value for Notes and Documents components
   */
  notesDocsFlex?: string;
};

/**
 * Form Page Layout
 */
export const ViewPage: React.FC<ViewPageProps> = observer(
  ({
    children,
    modelType,
    modelName,
    icon,
    speedDialActions,
    id,
    sidebar,
    sidebarItems,
    sidebarFlex = '25',
    contentFlex = '75',
    width,
    setSidebarItems,
    textClassName,
    modals,
    noteOptions,
    documentOptions,
    notesDocsFlex,
  }) => {
    /** Context **/
    const userStore = useUserStore();

    /** Computed **/
    const pageId = id || `${modelType.toLowerCase()}Form`;
    const modelIcon = icon ?? dataModelIcons[modelType];
    const title = modelName;
    const showSidebar = sidebar || (sidebarItems && setSidebarItems && (noteOptions || documentOptions));

    /** Render **/
    const renderNotes = (index = 0) =>
      sidebarItems &&
      setSidebarItems &&
      noteOptions && (
        <Notes
          mode={noteOptions.mode}
          notes={noteOptions.notes ?? []}
          sidebarIndex={index}
          sidebarItems={sidebarItems}
          setSidebarItems={setSidebarItems}
          updateNotes={noteOptions.onChange}
          title={noteOptions.title}
          tooltip={noteOptions.tooltip}
        />
      );

    const renderDocuments = (index = 1) =>
      sidebarItems &&
      setSidebarItems &&
      documentOptions && (
        <Documents
          mode={documentOptions.mode}
          documents={documentOptions.files ?? []}
          sidebarIndex={index}
          sidebarItems={sidebarItems}
          setSidebarItems={setSidebarItems}
          updateDocuments={documentOptions.onChange}
          title={documentOptions.title}
          tooltip={documentOptions.tooltip}
        />
      );

    return (
      <DesktopLayout>
        <Box id={pageId} className="detail-page-content md-default-theme">
          <Box layout-gt-sm="row">
            {showSidebar ? (
              <Box sx={{ flex: sidebarFlex }}>
                {/* SIDEBAR */}
                {sidebar}
                {sidebarItems && setSidebarItems && (noteOptions || documentOptions) && (
                  <Box sx={{ flex: notesDocsFlex }}>
                    {noteOptions && renderNotes()}
                    {documentOptions && renderDocuments()}
                  </Box>
                )}
              </Box>
            ) : (
              <Box display="flex" flex={1} />
            )}

            <Box sx={!width ? { flex: contentFlex } : undefined} width={width}>
              <Card component="md-card" style={{ background: 'white' }}>
                <ToolbarContainer>
                  <Box component="span" display="flex" flex={15}>
                    {userStore.isAdmin && speedDialActions && (
                      <FuseSpeedDial
                        ariaLabel="Adjustment Options"
                        direction="right"
                        icon={<Menu style={{ color: 'black' }} className="md-icon material-icons" />}
                      >
                        {Array.isArray(speedDialActions) ? speedDialActions.map((action) => action) : speedDialActions}
                      </FuseSpeedDial>
                    )}
                  </Box>

                  <Box
                    component="span"
                    justifyContent="center"
                    alignItems="center"
                    className={textClassName ?? 'thin md-display-1'}
                    display="flex"
                    gap="6px"
                  >
                    {modelIcon}
                    {title}
                  </Box>

                  <Box component="span" display="flex" flex={15} />
                </ToolbarContainer>

                {children}
              </Card>
            </Box>

            <Box display="flex" flex={1} />
          </Box>

          {modals}
        </Box>
      </DesktopLayout>
    );
  }
);
